import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../Components/Layout';
import { 
  Typography, 
  Box,
  Card,
  CardContent,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { FONT_FAMILY } from '../Constant';
import { validateToken } from '../redux/actions/validateAction';
import { toggleVoiceMode, resetVoiceState } from '../redux/actions/voiceActions';
import { showSuccess } from '../Components/reuse/Toast';
import useCallOnce from '../hooks/useCallOnce';

function Voices() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { toggling, error, success } = useSelector((state) => state.voice);
  
  const [localTextOnly, setLocalTextOnly] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);

  // Fetch initial voice setting using validateToken action - only once
  useCallOnce('voices/getVoiceSetting', () => {
    dispatch(validateToken());
  });

  // Update local state when user data changes
  useEffect(() => {
    if (user) {
      setLocalTextOnly(user.textOnly || false);
    }
  }, [user]);

  // Handle success message from toggle action
  useEffect(() => {
    if (success && !showSuccessMessage) {
      // Show toast message with the dynamic API response message
      showSuccess(success);
      setShowSuccessMessage(true);
      
      // Clear success message after 3 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, showSuccessMessage]);

  // Cleanup effect to reset voice state when component unmounts
  useEffect(() => {
    return () => {
      // Reset voice state when component unmounts
      dispatch(resetVoiceState());
    };
  }, [dispatch]);

  const handleVoiceToggle = (event) => {
    const newValue = event.target.checked;
    
    // Show confirmation dialog before making API call
    setPendingToggleValue(newValue);
    setShowConfirmation(true);
  };

  const handleConfirmToggle = async () => {
    if (pendingToggleValue === null) return;
    
    try {
      // Use the Redux action to toggle voice mode
      const result = await dispatch(toggleVoiceMode(pendingToggleValue));

      if (!result.success) {
        // Revert the toggle if update failed
        setLocalTextOnly(!pendingToggleValue);
      } else {
        // Update local state on success
        setLocalTextOnly(pendingToggleValue);
      }
    } catch (err) {
      console.error('Error toggling voice mode:', err);
      // Revert the toggle if update failed
      setLocalTextOnly(!pendingToggleValue);
    } finally {
      // Close confirmation dialog and reset pending value
      setShowConfirmation(false);
      setPendingToggleValue(null);
    }
  };

  const handleCancelToggle = () => {
    // Close confirmation dialog and reset pending value
    setShowConfirmation(false);
    setPendingToggleValue(null);
  };

  const getConfirmationMessage = () => {
    if (pendingToggleValue === null) return '';
    
    return pendingToggleValue 
      ? 'Are you sure you want to enable voice mode?'
      : 'Are you sure you want to disable voice mode?';
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 720, mt: 3, px: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Voices
        </Typography>

        {/* Voice Mode Box */}
        <Card 
          elevation={3} 
          sx={{ 
            borderRadius: 2, 
            mb: 3,
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#111827',
                  fontFamily: FONT_FAMILY
                }}
              >
                Voice Mode
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Active label - only show when textOnly is true */}
                {localTextOnly && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500, 
                      color: '#111827',
                      fontFamily: FONT_FAMILY
                    }}
                  >
                    Active
                  </Typography>
                )}
                
                {/* Switch toggle */}
                <Switch
                  checked={localTextOnly}
                  onChange={handleVoiceToggle}
                  disabled={toggling}
                  // sx={{
                  //   '& .MuiSwitch-switchBase.Mui-checked': {
                  //     color: '#ffffff',
                  //     '&:hover': {
                  //       backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  //     },
                  //   },
                  //   '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  //     backgroundColor: '#000000',
                  //   },
                  //   '& .MuiSwitch-track': {
                  //     backgroundColor: '#d1d5db',
                  //   },
                  //   '& .MuiSwitch-thumb': {
                  //     backgroundColor: localTextOnly ? '#ffffff' : '#ffffff',
                  //     border: '1px solid #d1d5db',
                  //   },
                  // }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Descriptive Text */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6b7280',
                fontFamily: FONT_FAMILY,
                fontSize: '14px',
                lineHeight: 1.5
              }}
            >
              Turning it on will activate agent audio and allow users to directly speak.
            </Typography>

            {/* Toggling State */}
            {toggling && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Updating voice mode...
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2,
                  '& .MuiAlert-message': { color: '#dc2626' },
                  '& .MuiAlert-icon': { color: '#dc2626' }
                }}
              >
                {error}
              </Alert>
            )}

            {/* Success Message - Only show when toggle action is performed */}
            {showSuccessMessage && success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 2,
                  '& .MuiAlert-message': { color: '#059669' },
                  '& .MuiAlert-icon': { color: '#059669' }
                }}
              >
                {success}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog 
          open={showConfirmation} 
          onClose={handleCancelToggle}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
            Confirm Voice Mode Change
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY, color: '#374151' }}>
              {getConfirmationMessage()}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={handleCancelToggle}
              variant="outlined"
              sx={{ 
                fontFamily: FONT_FAMILY,
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmToggle}
              variant="contained"
              disabled={toggling}
              sx={{ 
                fontFamily: FONT_FAMILY,
                backgroundColor: '#000000',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#1f2937'
                },
                '&:disabled': {
                  backgroundColor: '#9ca3af'
                }
              }}
            >
              {toggling ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default Voices;
